import type { ItemId } from "@skladiscenje/contracts";

import type { ApiClient } from "./client.js";
import type { CreateItemResponse, ItemFiltersQuery, ListItemsResponse, NewItemBody, WireItem } from "./types.js";

/**
 * Columns the entry-form autocomplete can query — mirrors
 * `SUGGEST_FIELDS`/`SuggestField` in `apps/server/src/repository/types.ts`.
 *
 * Not imported from there: that module is server-internal (not part of
 * `@skladiscenje/contracts`, and reaching into `apps/server/src/**` from
 * production client code — as opposed to the test-only harness in
 * `test-support/server.ts` — would blur the API boundary this app is
 * supposed to consume over HTTP). There is currently no shared package this
 * enum lives in, so it is duplicated here deliberately; see the report for
 * this gap.
 */
export const SUGGEST_FIELDS = ["name", "lastname", "phone", "carMaker", "registrationNumber", "tireMaker", "dimension"] as const;
export type SuggestField = (typeof SUGGEST_FIELDS)[number];

export function listItems(client: ApiClient, filters: ItemFiltersQuery): Promise<ListItemsResponse> {
  return client.get<ListItemsResponse>("/api/items", {
    seasonId: filters.seasonId,
    pickedUp: filters.pickedUp,
    search: filters.search,
    limit: filters.limit,
    offset: filters.offset,
  });
}

export function getItem(client: ApiClient, id: ItemId): Promise<{ item: WireItem }> {
  return client.get<{ item: WireItem }>(`/api/items/${id}`);
}

export function createItem(client: ApiClient, body: NewItemBody): Promise<CreateItemResponse> {
  return client.post<CreateItemResponse>("/api/items", body);
}

export function updateItem(
  client: ApiClient,
  id: ItemId,
  body: Omit<NewItemBody, "swapOutItemId">,
): Promise<{ item: WireItem }> {
  return client.patch<{ item: WireItem }>(`/api/items/${id}`, body);
}

export function markPickedUp(client: ApiClient, id: ItemId, employeeCode: string): Promise<{ item: WireItem }> {
  return client.post<{ item: WireItem }>(`/api/items/${id}/pickup`, { employeeCode });
}

export function revertPickup(client: ApiClient, id: ItemId, employeeCode: string): Promise<{ item: WireItem }> {
  return client.delete<{ item: WireItem }>(`/api/items/${id}/pickup`, { employeeCode });
}

export function deleteItem(client: ApiClient, id: ItemId, employeeCode: string): Promise<{ item: WireItem }> {
  return client.delete<{ item: WireItem }>(`/api/items/${id}`, { employeeCode });
}

export function restoreItem(client: ApiClient, id: ItemId, employeeCode: string): Promise<{ item: WireItem }> {
  return client.post<{ item: WireItem }>(`/api/items/${id}/restore`, { employeeCode });
}

export function suggestItems(
  client: ApiClient,
  field: SuggestField,
  q: string,
  limit = 10,
): Promise<{ suggestions: string[] }> {
  return client.get<{ suggestions: string[] }>("/api/items/suggestions", { field, q, limit });
}
